import type { useQuizResultActions } from '@mentimeter/quiz';
import {
  GameStateEnums,
  type GameStates,
  type Player,
  type QuizPlayerScore,
} from '@mentimeter/quiz-types';
import { RevealAnswer } from '../../shared/components/quiz/RevealAnswer';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { Results } from './results/Results';
import type { QuizOpenPresentationProps } from './types';

interface GameSatesProps extends QuizOpenPresentationProps {
  gameState: GameStates | undefined;
  timeOffset: number;
  connectedPlayers: Player[];
  respondents: string[];
  onReload: () => void;
  onMarkAnswer:
    | ReturnType<typeof useQuizResultActions>['markAnswer']
    | undefined;
  everyoneHasVoted: boolean;
  connectedWithDelay: boolean;
  totalQuizQuestions: number;
  currentQuizQuestionIndex: number;
  isReadonly: boolean;
  results: QuizPlayerScore[];
  isPreviewResults: boolean;
  shouldShowLegacyImage: boolean;
  imageUrl: string | undefined;
  timeBasedScoring: boolean;
}

export const GameStatesSwitcher = ({
  visibleCorrectAnswer,
  imageUrl,
  timeBasedScoring,
  title,
  styledTitle,
  label,
  description,
  textSizeOffset,
  isReadonly,
  results,
  isPreviewResults,
  gameState,
  timeOffset,
  connectedPlayers,
  respondents,
  onReload,
  everyoneHasVoted,
  connectedWithDelay,
  totalQuizQuestions,
  currentQuizQuestionIndex,
  onMarkAnswer,
  shouldShowLegacyImage,
}: GameSatesProps) => {
  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={title}
          styledTitle={styledTitle}
          label={label}
          fontSizeOffset={textSizeOffset}
        />
        {results.length > 0 && (
          <RevealAnswer correctAnswer={visibleCorrectAnswer}>
            <Results
              onMarkAnswer={onMarkAnswer}
              results={results}
              isReadonly={isReadonly}
            />
          </RevealAnswer>
        )}
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalQuizQuestions}
            current={currentQuizQuestionIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={timeBasedScoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={title}
            styledTitle={styledTitle}
            label={label}
            description={description}
            fontSizeOffset={textSizeOffset}
          />
          <CenteringContainer>
            <Countdown
              type="open"
              respondents={respondents.length}
              everyoneHasVoted={everyoneHasVoted}
              imageUrl={imageUrl}
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={title}
            styledTitle={styledTitle}
            label={label}
            fontSizeOffset={textSizeOffset}
          />
          <RevealAnswer correctAnswer={visibleCorrectAnswer}>
            <Results
              onMarkAnswer={onMarkAnswer}
              results={results}
              isReadonly={isReadonly}
            />
          </RevealAnswer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading onReload={onReload} />
        </CenteringContainer>
      );
    }
  }
};
